import { useMemo } from "react"

interface AnimatedTextHighlight {
  substring: string
  className: string
}

interface AnimatedTextProps {
  animation?: string
  preventUpdate?: boolean
  content: string | React.ReactNode
  highlight?: AnimatedTextHighlight
  tag?: "div" | "span" | "h1" | "h2" | "p"
  className?: string
}

function getSegmentProps(
  preventUpdate: boolean,
  otherProps: Record<string, unknown>,
): Record<string, string | boolean> {
  const segmentProps: Record<string, string | boolean> = {
    "data-animation": "webgl-text",
  }
  if (preventUpdate) {
    segmentProps["data-animation-prevent-update"] = true
  }
  if (otherProps["data-font-bold"] !== undefined) {
    segmentProps["data-font-bold"] =
      otherProps["data-font-bold"] === true ||
      otherProps["data-font-bold"] === ""
  }
  return segmentProps
}

export function AnimatedText({
  animation = "webgl-text",
  preventUpdate = false,
  content,
  highlight,
  tag = "div",
  className,
  ...otherProps
}: AnimatedTextProps) {
  const text = useMemo(() => {
    if (typeof content !== "string") {
      return ""
    }
    return content.replace(" ", "\u00A0") as string
  }, [content])

  if (!content || !text) {
    return null
  }

  const useHighlight =
    typeof content === "string" && highlight && highlight.substring.length > 0

  if (!useHighlight) {
    const props: Record<string, string | boolean> = {
      "data-animation": animation,
    }
    if (preventUpdate) {
      props["data-animation-prevent-update"] = true
    }
    const Comp = tag
    return (
      <Comp {...props} className={className} {...otherProps}>
        {content}
      </Comp>
    )
  }

  const index = text.indexOf(highlight!.substring)
  if (index === -1) {
    const props: Record<string, string | boolean> = {
      "data-animation": animation,
    }
    if (preventUpdate) {
      props["data-animation-prevent-update"] = true
    }
    const Comp = tag
    return (
      <Comp {...props} className={className} {...otherProps}>
        {content}
      </Comp>
    )
  }

  const before = text.slice(0, index)
  const segment = text.slice(index, index + highlight!.substring.length)
  const after = text.slice(index + highlight!.substring.length)

  const baseSegmentProps = getSegmentProps(preventUpdate, otherProps)
  const SegmentTag = tag
  const segmentStyle = { display: "inline" as const }

  return (
    <div className={className} {...otherProps}>
      {before && (
        <SegmentTag
          {...baseSegmentProps}
          {...otherProps}
          className={className}
          style={segmentStyle}
        >
          {before}
        </SegmentTag>
      )}
      <SegmentTag
        {...baseSegmentProps}
        {...otherProps}
        className={highlight!.className}
        style={segmentStyle}
      >
        {segment}
      </SegmentTag>
      {after && (
        <SegmentTag
          {...baseSegmentProps}
          {...otherProps}
          className={className}
          style={segmentStyle}
        >
          {after}
        </SegmentTag>
      )}
    </div>
  )
}
